import { ExtractionErrorType, ExtractionStatus } from "@inrev/common";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { DateTime } from "luxon";
import { FC, useId } from "react";
import { cn } from "../lib/utils";
import { Icon } from "../primitives";
import { PortaledTooltip } from "../ui/Tooltip";

const EXTRACTION_TIMESTAMP_FORMAT = "MM/dd/yyyy h:mm a";

export const extractionErrorTypeLabels: Record<ExtractionErrorType, string> = {
	PDF_CORRUPT: "PDF is corrupt",
	PDF_NOT_FINANCIAL: "PDF does not contain financial data",
	EXTRACTION_TIMEOUT: "Extraction timed out",
	LLM_ERROR: "LLM processing error",
	COMPONENT_NOT_FOUND: "Required component not found",
	UNKNOWN: "Extraction failed and requires review",
};

type ExtractionStatusIconProps = {
	status: ExtractionStatus | undefined;
	errorType?: ExtractionErrorType;
	errorMessage?: string;
	extractionTriggeredAt?: DateTime;
	extractionCompletedAt?: DateTime;
};

export const ExtractionStatusIcon: FC<ExtractionStatusIconProps> = ({
	status: rawStatus,
	errorType,
	errorMessage,
	extractionTriggeredAt,
	extractionCompletedAt,
}) => {
	const { isAdmin } = useAuthenticatedUser();
	const tooltipId = useId();
	const baseClass = "inline-flex h-7 w-7 items-center justify-center rounded-md border";

	if (!isAdmin && rawStatus === ExtractionStatus.Failed) {
		return (
			<>
				<span
					className={cn(baseClass, "border-bc-strong bg-surface-light text-tertiary")}
					data-tooltip-id={tooltipId}
					data-tooltip-content="An underwriter is reviewing this extraction."
				>
					<Icon type="user" size="mini" />
				</span>
				<PortaledTooltip id={tooltipId} />
			</>
		);
	}

	// Agents see "partial" extractions as "pending" to avoid exposing internal extraction states
	const displayStatus =
		!isAdmin && rawStatus === ExtractionStatus.Partial ? ExtractionStatus.Pending : rawStatus;

	if (displayStatus === ExtractionStatus.Completed) {
		const timeStamp = extractionCompletedAt?.toFormat(EXTRACTION_TIMESTAMP_FORMAT);
		const tooltip =
			isAdmin && timeStamp
				? `Extracted Successfully at ${timeStamp}`
				: "Extraction completed successfully.";
		return (
			<>
				<span
					className={cn(baseClass, "border-confirm-hover bg-surface-confirm text-confirm-hover")}
					data-tooltip-id={tooltipId}
					data-tooltip-content={tooltip}
				>
					<Icon type="check" size="mini" />
				</span>
				<PortaledTooltip id={tooltipId} />
			</>
		);
	}
	if (displayStatus === ExtractionStatus.Failed) {
		const baseLabel = extractionErrorTypeLabels[errorType ?? ExtractionErrorType.UNKNOWN];
		const errorLabel = errorMessage ? `${baseLabel} - ${errorMessage}` : baseLabel;
		const timeStamp = extractionCompletedAt?.toFormat(EXTRACTION_TIMESTAMP_FORMAT);
		const failedTooltip =
			isAdmin && timeStamp ? `Extraction Failed at ${timeStamp}: ${errorLabel}` : `${errorLabel}.`;
		return (
			<>
				<span
					className={cn(baseClass, "border-danger-hover bg-surface-base text-danger-hover")}
					data-tooltip-id={tooltipId}
					data-tooltip-content={failedTooltip}
				>
					<Icon type="x-circle" size="mini" />
				</span>
				<PortaledTooltip id={tooltipId} />
			</>
		);
	}
	if (displayStatus === ExtractionStatus.Partial) {
		const timeStamp = extractionCompletedAt?.toFormat(EXTRACTION_TIMESTAMP_FORMAT);
		const tooltip =
			isAdmin && timeStamp
				? `Extraction Partially Completed at ${timeStamp}`
				: "Extraction partially completed with issues.";
		return (
			<>
				<span
					className={cn(
						baseClass,
						"border-intent-warning-border bg-intent-warning-bg text-intent-warning-text",
					)}
					data-tooltip-id={tooltipId}
					data-tooltip-content={tooltip}
				>
					<Icon type="exclamation-triangle" size="mini" />
				</span>
				<PortaledTooltip id={tooltipId} />
			</>
		);
	}
	if (displayStatus === ExtractionStatus.Pending) {
		const timeStamp = extractionTriggeredAt?.toFormat(EXTRACTION_TIMESTAMP_FORMAT);
		const tooltip =
			isAdmin && timeStamp
				? `Extraction started at ${timeStamp}`
				: "Extraction is currently in progress.";
		return (
			<>
				<span
					className={cn(baseClass, "border-brand-primary bg-surface-base text-brand-primary")}
					data-tooltip-id={tooltipId}
					data-tooltip-content={tooltip}
				>
					<Icon type="clock" size="mini" />
				</span>
				<PortaledTooltip id={tooltipId} />
			</>
		);
	}
	if (displayStatus === ExtractionStatus.NotStarted) {
		return (
			<>
				<span
					className={cn(baseClass, "border-bc-heavy bg-surface-base text-secondary")}
					data-tooltip-id={tooltipId}
					data-tooltip-content="Extraction has not started yet."
				>
					<Icon type="minus-circle" size="mini" />
				</span>
				<PortaledTooltip id={tooltipId} />
			</>
		);
	}
	return (
		<>
			<span
				className="inline-flex h-7 w-7 items-center justify-center rounded-md text-tertiary"
				data-tooltip-id={tooltipId}
				data-tooltip-content="Not eligible for extraction."
			>
				N/A
			</span>
			<PortaledTooltip id={tooltipId} />
		</>
	);
};
